import useNotebookStore from '../../../../stores/notebookStore';
import { NotebookStore } from '../../../../stores/notebookStore/types';
import { shallow } from 'zustand/shallow';
import { useEffect, useCallback, useMemo, useState } from 'react';
import {
  useGetNotebookBoardSectionsQuery,
  usePostNotebookSectionsMutation,
  useDeleteNotebookSectionMutation,
} from '../../../../queries/Notebook';
import {
  NotebookViews,
  SectionFromAPI,
  StatusPayload,
} from '../../../../interfaces/notebook';
import { useProfileInfoFetchQuery } from '../../../../queries/Profile';
import { DropResult, DraggableLocation } from 'react-beautiful-dnd';
import isEmpty from 'lodash/isEmpty';
import { getMappedSectionsFromAPIResponse } from './utils';
import { trackNotebookActionEvent } from '../../../../Utils/analytics/notebook';
import { NOTEBOOK_EVENTS } from '../../../../Utils/analytics/constants';
import { getNotebookAnalyticsProps } from '../../../../Utils/notebook';

const notebookGroupSelector = (store: NotebookStore) => ({
  updateBoardSections: store.updateBoardSections,
  sectionOrder: store.sectionOrder,
  dragAndDropBoardViewTask: store.dragAndDropBoardViewTask,
  updateBoardSectionOrder: store.updateBoardSectionOrder,
  createBoardViewSection: store.createBoardViewSection,
  setCompletedSectionId: store.setCompletedSectionId,
  deleteBoardViewSection: store.deleteBoardViewSection,
  sectionalDropdownData: store.sectionalDropdownData,
});

export type NotebookNewSectionCreationData = {
  currentSectionId: string;
  position?: 'left' | 'right';
};

const getSectionCreationPayload = ({
  title,
  sectionOrder,
  newSectionCreationData,
}: {
  title: string;
  sectionOrder: string[];
  newSectionCreationData: NotebookNewSectionCreationData;
}) => {
  const { currentSectionId, position } = newSectionCreationData;
  const currentSectionIndex = sectionOrder.findIndex(
    (i) => i === currentSectionId,
  );
  if (currentSectionId && position && currentSectionIndex > -1) {
    if (position === 'left' && !isEmpty(currentSectionId)) {
      return {
        beforeId:
          currentSectionIndex === 0
            ? undefined
            : sectionOrder[currentSectionIndex - 1],
        title,
        afterId: currentSectionId,
      };
    }
    if (position === 'right' && !isEmpty(currentSectionId)) {
      return {
        beforeId: currentSectionId,
        title,
        afterId:
          currentSectionIndex === sectionOrder.length
            ? undefined
            : sectionOrder[currentSectionIndex + 1],
      };
    }
  }
  return {
    title,
    beforeId: sectionOrder[sectionOrder.length - 1],
  };
};

export const useNotebookBoardController = () => {
  const {
    sectionOrder,
    updateBoardSections,
    dragAndDropBoardViewTask,
    updateBoardSectionOrder,
    createBoardViewSection,
    setCompletedSectionId,
    deleteBoardViewSection,
    sectionalDropdownData,
  } = useNotebookStore(notebookGroupSelector, shallow);

  const [sectionTitle, setSectionTitle] = useState('');
  const [autoFocus, setAutoFocus] = useState(true);
  const boardData = useNotebookStore(
    useCallback((store) => store.boardNotes, []),
  );
  const [unassignedSectionId, setUnassignedSectionId] = useState<string | null>(
    null,
  );

  const unassignedSectionLength = useNotebookStore(
    useCallback(
      (store) =>
        unassignedSectionId !== null
          ? store.boardNotes[unassignedSectionId]?.tasks?.length
          : 0,
      [unassignedSectionId],
    ),
  );

  const [newSectionCreationData, setNewSectionCreationData] =
    useState<NotebookNewSectionCreationData>({
      currentSectionId: '',
      position: undefined,
    });

  const [showAddSectionButtonOnSuccess, toggleAddSectionButtonOnSuccess] =
    useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentFocusedSection, setCurrentFocusedSection] = useState<
    string | null
  >(null);

  const currentSectionLength = useMemo(() => {
    if (currentFocusedSection !== null)
      // TO DO: Sumedha - change tasks.length to total once backend starts working
      return boardData[currentFocusedSection]?.tasks?.length;
    return 0;
  }, [boardData, currentFocusedSection]);

  const [showDummySection, toggleDummySection] = useState(false);

  const currentFocusedSectionName = useNotebookStore(
    useCallback(
      (store) =>
        currentFocusedSection !== null &&
        store.boardSectionData[currentFocusedSection]
          ? store.boardSectionData[currentFocusedSection]?.name
          : '',
      [currentFocusedSection],
    ),
  );

  const sectionModalDropdownOptions = useMemo(() => {
    const filteredMenuItems = sectionalDropdownData.filter(
      (i) => i?.id !== currentFocusedSection && i?.id !== 'archiveAllTasks',
    );
    return [
      ...filteredMenuItems,
      {
        id: 'archiveAllTasks',
        value: 'Archive all tasks',
      },
    ];
  }, [currentFocusedSection, sectionalDropdownData]);

  const scrollAddNewSectionButtonIntoView = () => {
    const element = document.getElementById('add-new-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleSectionMutationSuccess = (
    sectionId: string,
    payload: StatusPayload,
  ) => {
    if (!sectionOrder.includes(sectionId)) {
      // If sectionId does not exist, we are CREATING a new section
      trackNotebookActionEvent({
        action: NOTEBOOK_EVENTS.NOTEBOOK_BOARD_SECTION_CREATED,
        view: NotebookViews.Board,
        sectionName: sectionTitle,
        sectionId,
      });
      createBoardViewSection(
        {
          sectionId: sectionId,
          name: sectionTitle,
          type: 'CUSTOM',
          count: 0,
        },
        newSectionCreationData,
      );
      toggleDummySection(!showAddSectionButtonOnSuccess);
      setSectionTitle('');
      setNewSectionCreationData({
        currentSectionId: '',
        position: undefined,
      });
      // Scroll the add button only when we are creating through the add button
      // In this case, because we are creating at the end
      // we are only going to have the beforeId (ID of the last section)
      if (
        !payload?.afterId &&
        payload?.beforeId === sectionOrder[sectionOrder.length - 1]
      ) {
        scrollAddNewSectionButtonIntoView();
      }
    }
  };

  const handleSectionDeleteMutationSuccess = (sectionId: string) => {
    deleteBoardViewSection(sectionId);
    trackNotebookActionEvent({
      action: NOTEBOOK_EVENTS.NOTEBOOK_BOARD_SECTION_DELETED,
      view: NotebookViews.Board,
      sectionName: currentFocusedSectionName,
      sectionId,
    });
    setCurrentFocusedSection(null);
    setNewSectionCreationData({
      currentSectionId: '',
      position: undefined,
    });
  };

  const { mutate: sectionsMutate, isLoading: isSectionBeingCreated } =
    usePostNotebookSectionsMutation(handleSectionMutationSuccess);

  const { mutate: sectionsDeleteMutate, isLoading: isSectionBeingDeleted } =
    useDeleteNotebookSectionMutation(handleSectionDeleteMutationSuccess);

  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useGetNotebookBoardSectionsQuery();
  const { data: profileInfo } = useProfileInfoFetchQuery();

  const currentUserTimezone = useMemo(
    () => profileInfo?.member.timeZone || '',
    [profileInfo?.member.timeZone],
  );

  useEffect(() => {
    if (!isLoading && !isError && data) {
      const { pages } = data;
      const sectionsFromAPI = pages.reduce<SectionFromAPI[]>(
        (arr, page) => [...arr, ...page.data],
        [],
      );
      const { sections, completedSection, unassignedSection } =
        getMappedSectionsFromAPIResponse(sectionsFromAPI);
      setCompletedSectionId(completedSection);
      setUnassignedSectionId(unassignedSection);
      updateBoardSections(sections);
    }
  }, [data, isLoading, isError, updateBoardSections, setCompletedSectionId]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      // dropped nowhere or dropped before NO SECTION
      if (!result.destination) {
        return;
      }

      const source: DraggableLocation = result.source;
      const destination: DraggableLocation = result.destination;

      // did not move anywhere - can bail early
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      // If user tries to move a section before NO SECTION with non zero tasks length, bail early
      if (
        result.type === 'COLUMN' &&
        destination?.index === 0 &&
        unassignedSectionLength > 0
      ) {
        return;
      }
      // reordering column
      if (result.type === 'COLUMN') {
        const updatedSectionOrder = [...sectionOrder];
        updatedSectionOrder.splice(source.index, 1);
        updatedSectionOrder.splice(destination.index, 0, result.draggableId);
        const beforeId =
          destination.index === 0
            ? undefined
            : updatedSectionOrder[destination.index - 1];
        const afterId =
          destination.index + 1 >= updatedSectionOrder.length
            ? undefined
            : updatedSectionOrder[destination.index + 1];
        updateBoardSectionOrder({
          sectionId: result.draggableId,
          source: result.source,
          destination: result.destination,
        });
        trackNotebookActionEvent({
          action: NOTEBOOK_EVENTS.NOTEBOOK_BOARD_SECTION_MOVED,
          view: NotebookViews.Board,
          sectionId: result.draggableId,
        });
        sectionsMutate({
          payload: {
            sectionId: result.draggableId,
            beforeId,
            afterId,
          },
        });
        return;
      }

      if (result.type === 'TASK') {
        dragAndDropBoardViewTask(
          {
            taskId: result.draggableId,
            source: result.source,
            destination: result.destination,
          },
          currentUserTimezone,
        );
        const notebookAnalyticsProps = getNotebookAnalyticsProps(
          result.source.droppableId,
          result.destination.droppableId,
        );
        trackNotebookActionEvent({
          action: NOTEBOOK_EVENTS.NOTEBOOK_SECTION_CHANGED,
          ...notebookAnalyticsProps,
        });
      }
    },
    [
      unassignedSectionLength,
      sectionOrder,
      updateBoardSectionOrder,
      sectionsMutate,
      dragAndDropBoardViewTask,
      currentUserTimezone,
    ],
  );

  const handleSectionCreationButtonClick = () => {
    toggleDummySection(true);
    setSectionTitle('');
  };

  const handleSectionCreation = useCallback(
    (title: string, isBlur = false) => {
      if (!isBlur) {
        setAutoFocus(true);
      } else {
        toggleAddSectionButtonOnSuccess(true);
      }
      if (!isEmpty(title)) {
        const sectionCreationPayload = getSectionCreationPayload({
          title,
          sectionOrder,
          newSectionCreationData,
        });

        setAutoFocus(true);
        sectionsMutate({
          payload: sectionCreationPayload,
        });
      } else {
        toggleDummySection(false);
        setNewSectionCreationData({
          currentSectionId: '',
          position: undefined,
        });
      }
    },
    [newSectionCreationData, sectionOrder, sectionsMutate],
  );

  const toggleModal = (
    toggle: boolean,
    currentSectionId: string | null,
    shouldMakeAPICall = false,
  ) => {
    setShowModal(toggle);
    setCurrentFocusedSection(currentSectionId);
    if (!toggle && shouldMakeAPICall && currentSectionId !== null) {
      sectionsDeleteMutate({
        targetSectionId: null,
        archiveAllTasks: true,
        deletedSectionId: currentSectionId,
      });
    }
  };

  const onPrimaryButtonClick = (value: string | undefined) => {
    if (value && currentFocusedSection !== null) {
      const payload =
        value === 'archiveAllTasks'
          ? {
              archiveAllTasks: true,
              deletedSectionId: currentFocusedSection,
            }
          : {
              targetSectionId: value,
              deletedSectionId: currentFocusedSection,
            };
      sectionsDeleteMutate(payload);
      setShowModal(false);
    }
  };

  const handleNewSectionCreationThroughMenu = (
    newSectionData: NotebookNewSectionCreationData,
  ) => {
    setSectionTitle('');
    setNewSectionCreationData(newSectionData);
  };

  return {
    boardSections: sectionOrder,
    dragAndDropBoardViewTask,
    updateBoardSectionOrder,
    isLoadingSections: !!isLoading,
    onDragEnd,
    createOrEditBoardViewSection: createBoardViewSection,
    showDummySection,
    isSectionBeingCreated,
    isSectionBeingDeleted,
    handleSectionCreationButtonClick,
    handleSectionCreation,
    toggleDummySection,
    autoFocus,
    sectionTitle,
    setSectionTitle,
    sectionModalDropdownOptions,
    showModal,
    toggleModal,
    currentFocusedSection,
    onPrimaryButtonClick,
    currentSectionLength,
    setNewSectionCreationData: handleNewSectionCreationThroughMenu,
    newSectionCreationData,
    currentFocusedSectionName,
  };
};
